import { useEffect, useState } from 'react';
import { getReferrals } from '@/lib/api';

interface FunilEtapa { etapa: string; n: number; pct: number }
interface LeaderRow {
  org_id: number; email: string | null; shares: number; aceitos: number;
  premiados: number; referidos_pagaram: number;
}
interface Recente {
  id: number; sharer_org_id: number; invited_email: string | null; status: string;
  referral_rewarded: boolean; processo_id: number | null; accepted_by_org_id: number | null;
  created_at: string | null; accepted_at: string | null;
}
interface Data {
  disponivel: boolean;
  erro?: string;
  config: { reward_creditos: number; cap_mensal: number; preco_credito: number };
  resumo: {
    criados: number; aceitos: number; premiados: number; sharers: number;
    orgs_referidas: number; ativaram: number; pagaram: number;
    creditos_concedidos: number; custo_creditos: number; receita_referidos: number;
    roi: number | null; self_ref_bloqueados: number;
  };
  funil: FunilEtapa[];
  viralidade: {
    k_factor: number; convites_por_sharer: number; taxa_conversao_cadastro: number;
    sharers: number; total_orgs: number;
  };
  qualidade: {
    referido: { n: number; ativacao_pct: number; pagamento_pct: number };
    organico: { n: number; ativacao_pct: number; pagamento_pct: number };
  };
  cap: { cap_mensal: number; sharers_no_cap: number };
  leaderboard: LeaderRow[];
  recentes: Recente[];
}

const BRL = (v: number) =>
  v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

function Card({ label, value, sub, accent }: { label: string; value: string; sub?: string; accent?: string }) {
  return (
    <div className="rounded-xl border border-slate-200 p-4 bg-white">
      <div className="text-xs text-slate-500">{label}</div>
      <div className={`text-3xl font-bold ${accent ?? 'text-slate-800'}`}>{value}</div>
      {sub && <div className="text-xs text-slate-400 mt-1">{sub}</div>}
    </div>
  );
}

export default function ReferralsPage() {
  const [d, setD] = useState<Data | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getReferrals().then((r) => setD(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-6 text-slate-400">Carregando…</div>;
  if (!d) return <div className="p-6 text-slate-400">Sem dados.</div>;
  if (!d.disponivel) return <div className="p-6 text-rose-500">Indisponível: {d.erro}</div>;

  const r = d.resumo;
  const k = d.viralidade;
  const q = d.qualidade;

  return (
    <div className="p-6 space-y-5">
      <div>
        <h1 className="text-xl font-semibold text-slate-800">Programa de Indicação (Referrals)</h1>
        <p className="text-sm text-slate-500">
          O link de compartilhamento de caso é o link de indicação. Recompensa:{' '}
          <b>{d.config.reward_creditos} crédito</b> por cadastro válido · cap{' '}
          <b>{d.config.cap_mensal}/mês</b>.
        </p>
      </div>

      {/* Resumo */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card label="Links criados" value={String(r.criados)} sub={`${r.sharers} indicadores`} />
        <Card label="Cadastros referidos" value={String(r.orgs_referidas)} accent="text-indigo-600" sub={`${r.aceitos} links aceitos`} />
        <Card label="Premiados" value={String(r.premiados)} sub={`${r.creditos_concedidos} créditos dados`} />
        <Card label="Referidos que pagaram" value={String(r.pagaram)} accent="text-emerald-600" sub={`${r.ativaram} ativaram`} />
      </div>

      {/* Economia do programa */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card label="Custo (créditos dados)" value={BRL(r.custo_creditos)} sub={`${r.creditos_concedidos} × ${BRL(d.config.preco_credito)}`} />
        <Card label="Receita dos referidos" value={BRL(r.receita_referidos)} accent="text-emerald-600" sub="Stripe (compras)" />
        <Card label="ROI" value={r.roi != null ? `${r.roi}×` : '—'} accent={r.roi && r.roi >= 1 ? 'text-emerald-600' : 'text-slate-800'} sub="receita ÷ custo" />
        <Card label="K-factor (viral)" value={String(k.k_factor)} accent={k.k_factor >= 1 ? 'text-emerald-600' : 'text-amber-600'} sub={`${k.convites_por_sharer} convites/indicador × ${k.taxa_conversao_cadastro}%`} />
      </div>

      {/* Funil */}
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <h2 className="text-sm font-semibold text-slate-700 mb-3">Funil de indicação</h2>
        <div className="space-y-2">
          {d.funil.map((e) => (
            <div key={e.etapa} className="flex items-center gap-3">
              <div className="w-44 text-xs text-slate-600">{e.etapa}</div>
              <div className="flex-1 bg-slate-100 rounded h-5 overflow-hidden">
                <div className="bg-indigo-400 h-5" style={{ width: `${Math.min(100, e.pct)}%` }} />
              </div>
              <div className="w-24 text-right text-xs text-slate-500">{e.n} ({e.pct}%)</div>
            </div>
          ))}
        </div>
      </div>

      {/* Qualidade referido × orgânico + cap/fraude */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <h2 className="text-sm font-semibold text-slate-700 mb-1">Qualidade: referido × orgânico</h2>
          <p className="text-xs text-slate-400 mb-3">Quem chega por indicação ativa/paga mais ou menos?</p>
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-slate-400">
              <tr><th className="text-left py-1">Coorte</th><th className="text-right">n</th><th className="text-right">Ativação</th><th className="text-right">Pagamento</th></tr>
            </thead>
            <tbody>
              <tr className="border-t border-slate-100">
                <td className="py-1.5 text-slate-700">Referidos</td>
                <td className="text-right">{q.referido.n}</td>
                <td className="text-right font-semibold text-indigo-600">{q.referido.ativacao_pct}%</td>
                <td className="text-right font-semibold text-emerald-600">{q.referido.pagamento_pct}%</td>
              </tr>
              <tr className="border-t border-slate-100">
                <td className="py-1.5 text-slate-700">Orgânicos</td>
                <td className="text-right">{q.organico.n}</td>
                <td className="text-right text-slate-600">{q.organico.ativacao_pct}%</td>
                <td className="text-right text-slate-600">{q.organico.pagamento_pct}%</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <h2 className="text-sm font-semibold text-slate-700 mb-1">Cap mensal & integridade</h2>
          <div className="grid grid-cols-2 gap-4 mt-3">
            <div>
              <div className="text-2xl font-bold text-slate-800">{d.cap.sharers_no_cap}</div>
              <div className="text-xs text-slate-400">indicadores no cap ({d.cap.cap_mensal}/mês) este mês</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-amber-600">{r.self_ref_bloqueados}</div>
              <div className="text-xs text-slate-400">autoindicações detectadas (mesma org)</div>
            </div>
          </div>
        </div>
      </div>

      {/* Leaderboard */}
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="px-4 py-2.5 border-b border-slate-100 text-sm font-semibold text-slate-700">
          Top indicadores ({d.leaderboard.length})
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-400">
            <tr>
              <th className="px-4 py-2 text-left">Indicador</th>
              <th className="px-3 py-2 text-right">Links</th>
              <th className="px-3 py-2 text-right">Aceitos</th>
              <th className="px-3 py-2 text-right">Premiados</th>
              <th className="px-3 py-2 text-right">Referidos pagaram</th>
            </tr>
          </thead>
          <tbody>
            {d.leaderboard.map((l) => (
              <tr key={l.org_id} className="border-t border-slate-100">
                <td className="px-4 py-2 text-slate-700">{l.email || `org #${l.org_id}`}</td>
                <td className="px-3 py-2 text-right text-slate-600">{l.shares}</td>
                <td className="px-3 py-2 text-right text-slate-600">{l.aceitos}</td>
                <td className="px-3 py-2 text-right font-semibold text-slate-800">{l.premiados}</td>
                <td className="px-3 py-2 text-right font-semibold text-emerald-600">{l.referidos_pagaram}</td>
              </tr>
            ))}
            {d.leaderboard.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-4 text-center text-slate-400">Nenhuma indicação ainda.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Recentes */}
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="px-4 py-2.5 border-b border-slate-100 text-sm font-semibold text-slate-700">
          Indicações recentes ({d.recentes.length})
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-400">
              <tr>
                <th className="px-3 py-2 text-left">Indicador (org)</th>
                <th className="px-3 py-2 text-left">Convidado</th>
                <th className="px-3 py-2 text-left">Caso</th>
                <th className="px-3 py-2 text-center">Status</th>
                <th className="px-3 py-2 text-center">Premiado</th>
                <th className="px-3 py-2 text-left">Quando</th>
              </tr>
            </thead>
            <tbody>
              {d.recentes.map((c) => (
                <tr key={c.id} className="border-t border-slate-50">
                  <td className="px-3 py-2 text-slate-600">#{c.sharer_org_id}</td>
                  <td className="px-3 py-2 text-slate-500 truncate max-w-[200px]">{c.invited_email || '—'}</td>
                  <td className="px-3 py-2 text-slate-500">{c.processo_id ? `#${c.processo_id}` : '—'}</td>
                  <td className="px-3 py-2 text-center">
                    <span className={`text-xs ${c.status === 'accepted' ? 'text-emerald-600' : 'text-slate-400'}`}>{c.status}</span>
                  </td>
                  <td className="px-3 py-2 text-center">{c.referral_rewarded ? '✓' : '—'}</td>
                  <td className="px-3 py-2 text-slate-400 text-xs">
                    {c.created_at ? new Date(c.created_at).toLocaleDateString('pt-BR') : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
